import { Pencil, Trash2 } from "lucide-react";
import { useCanEdit } from "@/model/access";
import { LinkDialog } from "./LinkDialog";
import { useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { analystGloss, fmtValue, fmtX, singleThreshold, stepBands, stepWord } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";
import type { RunResult } from "@/api/adapt";
import { useBase } from "@/hooks/world";
import { fmtByDate, idx, N } from "@/lib/months";
import { at, heroDate } from "@/lib/results";
import type { Link, MetricNode, Step } from "@/model/types";
import { StepLadder } from "./StepLadder";
import { EffectPreview } from "./EffectPreview";
import { NodeIcon } from "@/components/NodeIcon";

/**
 * Inline editable numbers: ink with a dotted underline (sage is kept for the baseline series), a hit
 * area of at least 24 px (44 px on touch) from an invisible pseudo-element, and a visible focus ring.
 */
export const inlineNumClass =
	"relative ml-1 cursor-pointer rounded-sm px-0.5 font-sans text-[0.93em] font-semibold text-ink underline decoration-ink/45 decoration-dotted decoration-1 underline-offset-4 after:absolute after:-inset-x-1 after:-inset-y-1.5 after:content-[''] hover:bg-accent hover:decoration-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background pointer-coarse:after:-inset-y-3.5 pointer-coarse:after:-inset-x-2";

/** A number inside a sentence that opens a slider. */
/** An inline number: editable for editors, plain text for viewers. */
export function Num(props: Parameters<typeof EditableNum>[0]) {
	const canEdit = useCanEdit();
	if (!canEdit)
		return (
			<span className="font-medium tabular-nums">
				{props.display}
				{props.suffix ? ` ${props.suffix}` : null}
			</span>
		);
	return <EditableNum {...props} />;
}

function EditableNum({
	value,
	display,
	label,
	min,
	max,
	step,
	onCommit,
	suffix,
	tone = "ink",
	format,
	parse,
	effect,
}: {
	value: number;
	display: string;
	/** What the number means, shown while editing ("Chance it follows"). */
	label: string;
	min: number;
	max: number;
	step: number;
	onCommit: (v: number) => void;
	suffix?: string;
	tone?: "ink" | "risk";
	format?: (v: number) => string;
	parse?: (s: string) => number;
	/** The link this number belongs to and how a value changes it, to preview the effect on its target. */
	effect?: { link: Link; patch: (v: number) => Partial<Link> };
}) {
	const [live, setLive] = useState<number | null>(null);
	const [text, setText] = useState<string | null>(null);
	const shown = live !== null ? (format ? format(live) : String(live)) : display;
	const typed = text !== null ? (parse ? parse(text) : parseFloat(text)) : NaN;
	const pending = live ?? (Number.isFinite(typed) ? Math.min(max, Math.max(min, typed)) : null);
	const draft = useMemo(
		() => (effect && pending !== null ? ({ ...effect.link, ...effect.patch(pending) } as Link) : null),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[effect?.link, pending],
	);
	const commitText = () => {
		if (text === null) return;
		if (Number.isFinite(typed)) onCommit(Math.min(max, Math.max(min, typed)));
		setText(null);
	};
	return (
		<Popover
			onOpenChange={() => {
				setLive(null);
				setText(null);
			}}
		>
			<PopoverTrigger asChild>
				<button className={cn(inlineNumClass, tone === "risk" && "text-[var(--risk-ink)] decoration-[var(--risk)]/45")} aria-label={`${label}: ${display}`}>
					{shown}
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-72 max-w-[calc(100vw-24px)] gap-3 p-3" side="bottom" align="center" collisionPadding={12} avoidCollisions>
				<div>
					<div className="text-xs text-muted-foreground">{label}</div>
					<div className="text-base font-semibold tabular-nums">
						{shown}
						{suffix ? <span className="font-normal text-muted-foreground"> {suffix}</span> : null}
					</div>
				</div>
				<Slider
					value={[live ?? value]}
					min={min}
					max={max}
					step={step}
					aria-label={label}
					onValueChange={([v]) => setLive(v)}
					onValueCommit={([v]) => {
						onCommit(v);
						setLive(null);
					}}
				/>
				<Input
					value={text ?? (format ? format(value) : String(value))}
					aria-label={`${label}, typed`}
					onChange={(e) => setText(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && commitText()}
					onBlur={commitText}
				/>
				{effect && <EffectPreview link={effect.link} draft={draft} />}
			</PopoverContent>
		</Popover>
	);
}

const pctParse = (s: string) => parseFloat(s.replace(/[%+]/g, "").replace(/^[−–]/, "-")) / 100;
const pctFmt = (v: number) => `${Math.round(v * 100)}%`;
const mo = (n: number) => `${n} ${n === 1 ? "month" : "months"}`;

function NodeRef({ id, strong }: { id: string; strong?: boolean }) {
	const node = useStore((s) => s.world.nodes[id]);
	const [params] = useSearchParams();
	if (!node) return <span>{id}</span>;
	const m = params.get("m");
	return (
		<RouterLink to={`/node/${id}${m ? `?m=${m}` : ""}`} className={"hover:underline " + (strong ? "font-medium" : "")}>
			<NodeIcon node={node} className="mr-1 text-[0.85em] text-muted-foreground" />
			{node.title}
		</RouterLink>
	);
}

/** A ladder of more than two steps as a small band → effect table (review I4). */
export function LadderTable({ steps, unit, className }: { steps: Step[]; unit: string; className?: string }) {
	return (
		<table className={cn("mt-1 w-full max-w-sm font-sans text-[13px]", className)}>
			<tbody>
				{stepBands(steps, unit).map((b, i) => (
					<tr key={i} className="border-t border-border/60 first:border-t-0">
						<td className="py-1 pr-3 text-muted-foreground tabular-nums">{b.label}</td>
						<td className={cn("py-1 tabular-nums", b.x === 1 ? "text-muted-foreground" : "font-medium text-ink")}>{stepWord(b.x)}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

/** One link as its plain sentence, with every number draggable (PRD §10.4). */
export function LinkRow({ link, onRemove }: { link: Link; onRemove?: () => void }) {
	const canEdit = useCanEdit();
	const update = useStore((s) => s.updateLink);
	const analyst = useStore((s) => s.analyst);
	const nodes = useStore((s) => s.world.nodes);
	const [editing, setEditing] = useState(false);
	const set = (patch: Partial<Link>) => update(link.id, patch);
	const fx = (patch: (v: number) => Partial<Link>) => ({ link, patch });

	let body: React.ReactNode;
	let table: React.ReactNode = null;
	let ladderMetric: MetricNode | null = null;
	if (link.type === "trigger") {
		body = (
			<>
				If <NodeRef id={link.from} /> happens,
				<Num
					label="Chance it follows"
					value={link.p}
					display={pctFmt(link.p)}
					min={0.01}
					max={1}
					step={0.01}
					format={pctFmt}
					parse={pctParse}
					onCommit={(p) => set({ p })}
					effect={fx((p) => ({ p }))}
				/>{" "}
				chance <NodeRef id={link.to} /> follows within
				<Num
					label="Within"
					value={link.within_months}
					display={mo(link.within_months)}
					min={1}
					max={60}
					step={1}
					format={mo}
					parse={parseFloat}
					onCommit={(v) => set({ within_months: Math.round(v) })}
					effect={fx((v) => ({ within_months: Math.round(v) }))}
				/>
				{link.lag_months ? (
					<>
						after a
						<Num
							label="Delay before it starts"
							value={link.lag_months}
							display={mo(link.lag_months)}
							min={0}
							max={36}
							step={1}
							format={mo}
							parse={parseFloat}
							onCommit={(v) => set({ lag_months: Math.round(v) })}
							effect={fx((v) => ({ lag_months: Math.round(v) }))}
						/>{" "}
						delay
					</>
				) : null}
				.
			</>
		);
	} else if (link.type === "multiplier" && "steps" in link) {
		const m = nodes[link.from.metric] as MetricNode | undefined;
		ladderMetric = m ?? null;
		const single = singleThreshold(link.steps);
		if (single) {
			body = (
				<>
					While <NodeRef id={link.from.metric} /> is {single.below ? "under" : "at or over"} {fmtValue(single.value, m?.unit ?? "")}, <NodeRef id={link.to} />{" "}
					is {fmtX(single.x)} as likely.
				</>
			);
		} else if (link.steps.length > 2) {
			body = (
				<>
					How likely <NodeRef id={link.to} /> is depends on <NodeRef id={link.from.metric} />:
				</>
			);
			table = <LadderTable steps={link.steps} unit={m?.unit ?? ""} />;
		} else {
			body = (
				<>
					How likely <NodeRef id={link.to} /> is depends on <NodeRef id={link.from.metric} />:{" "}
					{stepBands(link.steps, m?.unit ?? "")
						.map((b) => `${b.label}: ${stepWord(b.x)}`)
						.join("; ")}
					.
				</>
			);
		}
	} else if (link.type === "multiplier") {
		// Multipliers below 1 are a direction, not a loss: they stay neutral (review H5).
		const xNum = (
			<Num
				label="How much more likely"
				value={link.x}
				display={fmtX(link.x)}
				min={0}
				max={10}
				step={0.1}
				format={fmtX}
				parse={(s) => parseFloat(s.replace("×", ""))}
				onCommit={(x) => set({ x: Math.round(x * 10) / 10 })}
				suffix="as likely"
				effect={fx((x) => ({ x: Math.round(x * 10) / 10 }))}
			/>
		);
		body = (
			<>
				{link.for_months ? (
					<>
						For
						<Num
							label="For how long"
							value={link.for_months}
							display={mo(link.for_months)}
							min={1}
							max={60}
							step={1}
							format={mo}
							parse={parseFloat}
							onCommit={(v) => set({ for_months: Math.round(v) })}
							effect={fx((v) => ({ for_months: Math.round(v) }))}
						/>{" "}
						after <NodeRef id={link.from} />,
					</>
				) : (
					<>
						Once <NodeRef id={link.from} /> happens,
					</>
				)}{" "}
				<NodeRef id={link.to} /> is{xNum} as likely.
			</>
		);
	} else {
		body = (
			<>
				If <NodeRef id={link.from} /> happens, <NodeRef id={link.to} /> {link.change < 0 ? "drops" : "rises"}
				<Num
					label="Change"
					value={link.change}
					display={`${Math.round(Math.abs(link.change) * 100)}%`}
					min={-1}
					max={1}
					step={0.05}
					format={(v) => `${v < 0 ? "−" : "+"}${Math.round(Math.abs(v) * 100)}%`}
					parse={pctParse}
					tone={link.change < 0 ? "risk" : "ink"}
					onCommit={(change) => set({ change })}
					effect={fx((change) => ({ change }))}
				/>{" "}
				over
				<Num
					label="Over"
					value={link.over_months}
					display={mo(link.over_months)}
					min={1}
					max={36}
					step={1}
					format={mo}
					parse={parseFloat}
					onCommit={(v) => set({ over_months: Math.round(v) })}
					effect={fx((v) => ({ over_months: Math.round(v) }))}
				/>
				{link.recovery_months ? (
					<>
						, recovering over
						<Num
							label="Recovers over"
							value={link.recovery_months}
							display={mo(link.recovery_months)}
							min={1}
							max={60}
							step={1}
							format={mo}
							parse={parseFloat}
							onCommit={(v) => set({ recovery_months: Math.round(v) })}
							effect={fx((v) => ({ recovery_months: Math.round(v) }))}
						/>
					</>
				) : null}
				.
			</>
		);
	}

	const content = (
		<div className="min-w-0 flex-1">
			<p className="font-serif text-[14.5px] leading-relaxed">{body}</p>
			{table}
			{analyst && <p className="mt-0.5 text-xs text-muted-foreground">{analystGloss(link)}</p>}
		</div>
	);

	return (
		<div className="group flex items-start gap-2 py-2">
			{ladderMetric && link.type === "multiplier" && "steps" in link ? (
				<LadderPopover link={link} steps={link.steps} metric={ladderMetric} onCommit={(steps) => set({ steps } as Partial<Link>)}>
					{content}
				</LadderPopover>
			) : (
				content
			)}
			<div className={cn("flex shrink-0 opacity-50 group-hover:opacity-100 focus-within:opacity-100", !canEdit && "hidden")}>
				<Button variant="ghost" size="icon" className="size-8" onClick={() => setEditing(true)} aria-label="Edit link" title="Edit link">
					<Pencil className="size-4" />
				</Button>
				{onRemove && (
					<Button variant="ghost" size="icon" className="size-8" onClick={onRemove} aria-label="Remove link" title="Remove link">
						<Trash2 className="size-4" />
					</Button>
				)}
			</div>
			<LinkDialog open={editing} onOpenChange={setEditing} edit={link} />
		</div>
	);
}

/** The metric's low / likely / high at its headline date in the model, for context on the ladder's axis. */
export function ladderContext(m: MetricNode, run: RunResult | null) {
	const q = run?.metrics[m.id]?.q;
	if (!q) return null;
	const date = heroDate(m);
	const t = Math.min(N - 1, Math.max(0, idx(date)));
	return { lo: at(q[0], t), mid: at(q[2], t), hi: at(q[4], t), label: `likely by ${fmtByDate(date)}` };
}

/** "Edit steps" opens the ladder in a popover anchored to the sentence, so the side panel keeps its height (review C5). */
function LadderPopover({
	link,
	steps,
	metric,
	onCommit,
	children,
}: {
	link: Link;
	steps: Step[];
	metric: MetricNode;
	onCommit: (s: Step[]) => void;
	children: React.ReactNode;
}) {
	const [draft, setDraft] = useState<Step[] | null>(null);
	const base = useBase();
	const preview = useMemo(() => (draft ? ({ ...link, steps: draft } as Link) : null), [link, draft]);
	return (
		<Popover onOpenChange={() => setDraft(null)}>
			<PopoverAnchor asChild>
				<div className="min-w-0 flex-1">
					{children}
					<PopoverTrigger asChild>
						<button className="-ml-1 inline-flex h-7 items-center rounded-sm px-1 text-xs font-medium text-ink underline decoration-ink/45 decoration-dotted underline-offset-4 hover:decoration-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:h-11">
							Edit steps
						</button>
					</PopoverTrigger>
				</div>
			</PopoverAnchor>
			<PopoverContent className="w-[26rem] max-w-[calc(100vw-24px)] gap-3 p-3" side="bottom" align="start" collisionPadding={12} avoidCollisions>
				<div className="text-xs text-muted-foreground">How much more or less likely, by the level of {metric.title}</div>
				<StepLadder
					steps={draft ?? steps}
					unit={metric.unit}
					scale={metric.scale}
					context={ladderContext(metric, base.data)}
					onChange={setDraft}
					onCommit={(s) => {
						setDraft(null);
						onCommit(s);
					}}
					height={150}
				/>
				<EffectPreview link={link} draft={preview} />
			</PopoverContent>
		</Popover>
	);
}

export function LinkTypeIcon({ type }: { type: Link["type"] }) {
	return <span className="text-xs text-muted-foreground">{{ trigger: "Trigger", multiplier: "Multiplier", impact: "Impact" }[type]}</span>;
}
